(() => {
  const config = window.TOTheme || {};
  const routes = config.routes || {};
  const strings = config.strings || {};
  let returnFocus = null;
  let toastTimer = null;

  const getDialog = (id) => document.getElementById(id);

  function showToast(message) {
    const toast = document.querySelector('[data-toast]');
    if (!toast || !message) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
    }, 3200);
  }

  function openDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    document.querySelectorAll('dialog[open]').forEach((open) => {
      if (open !== dialog) open.close();
    });
    returnFocus = trigger || document.activeElement;
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('is-locked');
    window.setTimeout(() => {
      const focusTarget = dialog.querySelector('[data-dialog-focus], input:not([type="hidden"]), button, a[href]');
      if (focusTarget) focusTarget.focus();
    }, 20);
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('is-locked');
    if (returnFocus && document.contains(returnFocus)) returnFocus.focus();
    returnFocus = null;
  }

  function currentCartDrawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  function updateCartCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      if (element.closest('.header-action')) element.hidden = count === 0;
    });
  }

  async function refreshCartDrawer(shouldOpen = false) {
    const sectionUrl = `${routes.root || '/'}?section_id=cart-drawer`;
    const response = await fetch(sectionUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(strings.cartError || 'Cart error');

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const replacement = documentFragment.querySelector('[data-cart-drawer-section]');
    const current = document.querySelector('[data-cart-drawer-section]');
    if (replacement && current) current.replaceWith(replacement);

    const countElement = replacement && replacement.querySelector('[data-cart-count]');
    const count = countElement ? Number.parseInt(countElement.textContent, 10) || 0 : 0;
    updateCartCounts(count);

    if (shouldOpen) openDialog(currentCartDrawer());
  }

  async function updateCartLine(line, quantity) {
    const drawer = currentCartDrawer();
    if (drawer) drawer.classList.add('is-loading');
    try {
      const response = await fetch(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!response.ok) throw new Error(strings.cartError || 'Cart error');
      await refreshCartDrawer(true);
    } catch (error) {
      const message = error.message || strings.cartError || 'Cart error';
      const errorElement = currentCartDrawer() && currentCartDrawer().querySelector('[data-cart-error]');
      if (errorElement) {
        errorElement.textContent = message;
        errorElement.hidden = false;
      } else {
        showToast(message);
      }
    } finally {
      const activeDrawer = currentCartDrawer();
      if (activeDrawer) activeDrawer.classList.remove('is-loading');
    }
  }

  function getProductVariant(root) {
    const data = root.querySelector('[data-product-variants]');
    if (!data) return null;

    let variants = [];
    try {
      variants = JSON.parse(data.textContent);
    } catch (_error) {
      return null;
    }

    const selectedOptions = Array.from(root.querySelectorAll('[data-option-position]'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((fieldset) => {
        const selected = fieldset.querySelector('input:checked');
        return selected ? selected.value : null;
      });

    return variants.find((variant) => variant.options.every((option, index) => option === selectedOptions[index])) || null;
  }

  function gallerySlides(gallery) {
    return gallery ? Array.from(gallery.querySelectorAll('[data-media-slide]')) : [];
  }

  function mediaScrollTarget(track, slide) {
    if (!track || !slide) return 0;
    const styles = window.getComputedStyle(track);
    const inset = Number.parseFloat(styles.scrollPaddingLeft) || Number.parseFloat(styles.paddingLeft) || 0;
    return Math.max(0, slide.offsetLeft - inset);
  }

  function updateProductMediaState(gallery, requestedIndex) {
    const slides = gallerySlides(gallery);
    if (!slides.length) return 0;

    const index = ((Number(requestedIndex) % slides.length) + slides.length) % slides.length;
    gallery.dataset.mediaIndex = String(index);

    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === index);
    });

    gallery.querySelectorAll('[data-media-thumb]').forEach((thumb, thumbIndex) => {
      const isActive = thumbIndex === index;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', String(isActive));
    });

    const current = gallery.querySelector('[data-media-current]');
    if (current) current.textContent = String(index + 1).padStart(2, '0');

    const progress = gallery.querySelector('[data-media-progress]');
    if (progress) progress.style.setProperty('--media-progress', String((index + 1) / slides.length));

    return index;
  }

  function selectProductMedia(gallery, requestedIndex, options = {}) {
    if (!gallery) return;
    const slides = gallerySlides(gallery);
    const index = updateProductMediaState(gallery, requestedIndex);
    const track = gallery.querySelector('[data-media-track]');
    const shouldScroll = options.scroll !== false && window.matchMedia('(max-width: 989px)').matches;

    if (shouldScroll && track && slides[index]) {
      track.scrollTo({
        left: mediaScrollTarget(track, slides[index]),
        behavior: options.behavior || 'smooth'
      });
    }
  }

  function initializeProductGallery(gallery) {
    if (!gallery || gallery.dataset.mediaReady === 'true') return;
    gallery.dataset.mediaReady = 'true';
    updateProductMediaState(gallery, Number(gallery.dataset.mediaIndex || 0));

    const track = gallery.querySelector('[data-media-track]');
    if (!track) return;

    let scrollFrame = null;
    track.addEventListener('scroll', () => {
      if (!window.matchMedia('(max-width: 989px)').matches) return;
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const slides = gallerySlides(gallery);
        if (!slides.length) return;
        const index = slides.reduce((closestIndex, slide, slideIndex) => {
          const currentDistance = Math.abs(mediaScrollTarget(track, slides[closestIndex]) - track.scrollLeft);
          const candidateDistance = Math.abs(mediaScrollTarget(track, slide) - track.scrollLeft);
          return candidateDistance < currentDistance ? slideIndex : closestIndex;
        }, 0);
        updateProductMediaState(gallery, index);
      });
    }, { passive: true });
  }

  function initializeProductGalleries(scope = document) {
    scope.querySelectorAll('[data-product-gallery]').forEach(initializeProductGallery);
  }

  function mediaDialogTriggers(dialog) {
    const root = dialog && dialog.closest('[data-product-root]');
    return root ? Array.from(root.querySelectorAll('[data-media-open]')) : [];
  }

  function updateMediaDialog(dialog, requestedPosition) {
    const triggers = mediaDialogTriggers(dialog);
    const image = dialog && dialog.querySelector('[data-media-dialog-image]');
    if (!dialog || !image || !triggers.length) return;

    const position = ((Number(requestedPosition) % triggers.length) + triggers.length) % triggers.length;
    const trigger = triggers[position];
    const previewImage = trigger.querySelector('img');
    const previewSource = previewImage && (previewImage.currentSrc || previewImage.src);
    const fullSource = trigger.dataset.mediaSrc;
    const requestKey = `${position}-${Date.now()}`;
    dialog.dataset.mediaPosition = String(position);
    dialog.dataset.mediaRequest = requestKey;
    image.src = previewSource || fullSource;
    image.alt = trigger.dataset.mediaAlt || '';
    image.width = Number(trigger.dataset.mediaWidth) || image.width;
    image.height = Number(trigger.dataset.mediaHeight) || image.height;

    if (previewSource && fullSource && previewSource !== fullSource) {
      const fullImage = new Image();
      fullImage.alt = image.alt;
      fullImage.width = image.width;
      fullImage.height = image.height;
      fullImage.setAttribute('data-media-dialog-image', '');

      const showFullImage = () => {
        if (dialog.dataset.mediaRequest !== requestKey) return;
        const currentImage = dialog.querySelector('[data-media-dialog-image]');
        if (currentImage) currentImage.replaceWith(fullImage);
      };
      fullImage.src = fullSource;

      if (typeof fullImage.decode === 'function') {
        fullImage.decode().then(showFullImage).catch(() => {
          if (fullImage.complete && fullImage.naturalWidth > 0) showFullImage();
        });
      } else {
        fullImage.onload = showFullImage;
      }
    }

    const current = dialog.querySelector('[data-dialog-media-current]');
    if (current) current.textContent = String(position + 1).padStart(2, '0');
  }

  function openProductMediaDialog(root, trigger) {
    const dialog = root && root.querySelector('[data-product-media-dialog]');
    if (!dialog || !trigger) return;
    const triggers = mediaDialogTriggers(dialog);
    const position = Math.max(0, triggers.indexOf(trigger));
    updateMediaDialog(dialog, position);
    openDialog(dialog, trigger);
  }

  function updateProductVariant(root) {
    const variant = getProductVariant(root);
    const idInput = root.querySelector('[data-variant-id]');
    const button = root.querySelector('[data-add-to-cart]');
    const label = root.querySelector('[data-add-label]');
    const price = root.querySelector('[data-price]');

    if (!variant) {
      if (idInput) idInput.value = '';
      if (button) button.disabled = true;
      if (label) label.textContent = strings.unavailable || 'Unavailable';
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (button) button.disabled = !variant.available;
    if (label) label.textContent = variant.available ? (strings.addToCart || 'Add to bag') : (strings.soldOut || 'Sold out');

    if (price) {
      const regular = document.createElement('span');
      regular.className = 'price__regular';
      regular.textContent = variant.price;
      price.replaceChildren(regular);
      if (variant.compareAt && variant.compareAt !== variant.price) {
        const compare = document.createElement('s');
        compare.className = 'price__compare';
        compare.textContent = variant.compareAt;
        price.append(compare);
      }
    }

    if (variant.url) window.history.replaceState({}, '', variant.url);

    if (variant.featuredMediaId) {
      const media = root.querySelector(`[data-media-id="${variant.featuredMediaId}"]`);
      const gallery = root.querySelector('[data-product-gallery]');
      if (media && gallery) selectProductMedia(gallery, Number(media.dataset.mediaIndex));
    }
  }

  async function addProduct(form) {
    const button = form.querySelector('[data-add-to-cart]');
    const errorElement = form.querySelector('[data-product-error]');
    if (button) button.disabled = true;
    if (errorElement) errorElement.hidden = true;

    try {
      const response = await fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || strings.cartError || 'Cart error');
      await refreshCartDrawer(Boolean(document.querySelector('[data-cart-open]')));
      if (!document.querySelector('[data-cart-open]')) showToast(strings.addToCart || 'Added to bag');
    } catch (error) {
      if (errorElement) {
        errorElement.textContent = error.message;
        errorElement.hidden = false;
      } else {
        showToast(error.message);
      }
    } finally {
      const root = form.closest('[data-product-root]');
      const variant = root && getProductVariant(root);
      if (button) button.disabled = variant ? !variant.available : false;
    }
  }

  document.addEventListener('click', (event) => {
    const mediaThumb = event.target.closest('[data-media-thumb]');
    if (mediaThumb) {
      event.preventDefault();
      selectProductMedia(mediaThumb.closest('[data-product-gallery]'), Number(mediaThumb.dataset.mediaIndex));
      return;
    }

    const mediaStep = event.target.closest('[data-media-step]');
    if (mediaStep) {
      event.preventDefault();
      const gallery = mediaStep.closest('[data-product-gallery]');
      const currentIndex = Number(gallery && gallery.dataset.mediaIndex || 0);
      selectProductMedia(gallery, currentIndex + Number(mediaStep.dataset.mediaStep));
      return;
    }

    const dialogMediaStep = event.target.closest('[data-dialog-media-step]');
    if (dialogMediaStep) {
      event.preventDefault();
      const dialog = dialogMediaStep.closest('[data-product-media-dialog]');
      const currentPosition = Number(dialog && dialog.dataset.mediaPosition || 0);
      updateMediaDialog(dialog, currentPosition + Number(dialogMediaStep.dataset.dialogMediaStep));
      return;
    }

    const dialogTrigger = event.target.closest('[data-dialog-open]');
    if (dialogTrigger) {
      event.preventDefault();
      const openDialogElement = dialogTrigger.closest('dialog[open]');
      if (openDialogElement) openDialogElement.close();
      openDialog(getDialog(dialogTrigger.dataset.dialogOpen), dialogTrigger);
      return;
    }

    const dialogClose = event.target.closest('[data-dialog-close]');
    if (dialogClose) {
      event.preventDefault();
      closeDialog(dialogClose.closest('dialog'));
      return;
    }

    const cartOpen = event.target.closest('[data-cart-open]');
    if (cartOpen) {
      event.preventDefault();
      openDialog(currentCartDrawer(), cartOpen);
      return;
    }

    const cartClose = event.target.closest('[data-cart-close]');
    if (cartClose) {
      event.preventDefault();
      closeDialog(cartClose.closest('dialog'));
      return;
    }

    const cartQuantity = event.target.closest('[data-cart-quantity]');
    if (cartQuantity) {
      event.preventDefault();
      updateCartLine(Number(cartQuantity.dataset.line), Math.max(0, Number(cartQuantity.dataset.quantity)));
      return;
    }

    const cartRemove = event.target.closest('[data-cart-remove]');
    if (cartRemove) {
      event.preventDefault();
      updateCartLine(Number(cartRemove.dataset.line), 0);
      return;
    }

    const stepper = event.target.closest('[data-quantity-step]');
    if (stepper) {
      event.preventDefault();
      const input = stepper.closest('[data-quantity-control]').querySelector('input');
      if (!input) return;
      const minimum = Number(input.min || 0);
      input.value = Math.max(minimum, Number(input.value || minimum) + Number(stepper.dataset.quantityStep));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const mediaTrigger = event.target.closest('[data-media-open]');
    if (mediaTrigger) {
      event.preventDefault();
      const root = mediaTrigger.closest('[data-product-root]');
      const gallery = mediaTrigger.closest('[data-product-gallery]');
      if (gallery) updateProductMediaState(gallery, Number(mediaTrigger.dataset.mediaIndex));
      openProductMediaDialog(root, mediaTrigger);
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.tagName === 'DIALOG' && event.target.open) closeDialog(event.target);
  });

  document.addEventListener('close', () => {
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('is-locked');
    if (returnFocus && document.contains(returnFocus)) returnFocus.focus();
    returnFocus = null;
  }, true);

  document.addEventListener('keydown', (event) => {
    const dialog = document.querySelector('[data-product-media-dialog][open]');
    if (!dialog || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
    event.preventDefault();
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    updateMediaDialog(dialog, Number(dialog.dataset.mediaPosition || 0) + direction);
  });

  let dialogTouchStart = null;
  document.addEventListener('touchstart', (event) => {
    if (!event.target.closest('[data-product-media-dialog][open] .media-dialog__stage')) return;
    const touch = event.touches[0];
    dialogTouchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
  }, { passive: true });

  document.addEventListener('touchend', (event) => {
    const dialog = event.target.closest('[data-product-media-dialog][open]');
    const touch = event.changedTouches[0];
    if (!dialog || !touch || !dialogTouchStart) return;
    const deltaX = touch.clientX - dialogTouchStart.x;
    const deltaY = touch.clientY - dialogTouchStart.y;
    dialogTouchStart = null;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    updateMediaDialog(dialog, Number(dialog.dataset.mediaPosition || 0) + (deltaX < 0 ? 1 : -1));
  }, { passive: true });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-product-form] input[type="radio"]')) {
      const root = event.target.closest('[data-product-root]');
      if (root) updateProductVariant(root);
    }

    if (event.target.matches('[data-sort-select]')) event.target.form.submit();
    if (event.target.matches('[data-localization-select]')) event.target.form.submit();
  });

  document.addEventListener('submit', (event) => {
    const productForm = event.target.closest('[data-product-form]');
    if (!productForm) return;
    event.preventDefault();
    addProduct(productForm);
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-root]').forEach(updateProductVariant);
    initializeProductGalleries(event.target);
    event.target.querySelectorAll('select[data-default]').forEach((select) => {
      if (select.dataset.default) select.value = select.dataset.default;
    });
  });

  document.querySelectorAll('select[data-default]').forEach((select) => {
    if (select.dataset.default) select.value = select.dataset.default;
  });

  initializeProductGalleries();

  let galleryResizeTimer = null;
  window.addEventListener('resize', () => {
    window.clearTimeout(galleryResizeTimer);
    galleryResizeTimer = window.setTimeout(() => {
      document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
        selectProductMedia(gallery, Number(gallery.dataset.mediaIndex || 0), { behavior: 'auto' });
      });
    }, 120);
  });
})();
